// React.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import ChartPlotLine from "../../../types/ChartPlotLine";
import CombinedChanges from "../../../types/instance/CombinedChanges";

// Helper.
import {
    fetchResults,
    getConvertedTimeToUTC
} from '../../../helpers/utils';
import SqlApi from '../../../api/instance/SqlApi';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery, buildApiQuery } from "../../../helpers/timeRangeUtils";
import { useEventTypes } from "../../../context/EventTypeContext";
import Helper from "../../../helpers/Helper";
import TimeSeriesChart from "../../../component/chart/echarts/TimeSeriesChart";
import { tooltipRow, wrapTooltip } from "../../../component/chart/echarts/tooltipAdapter";
import { EChartBandPoint, EChartPoint, EChartTimeSeries } from "../../../component/chart/echarts/types";
import {
    getInstanceTotalDuration,
    getStatementExecutions,
    getStatementTypeFromURL,
    StatementTypeInfo
} from "../../instances/tabs/utils";

// Components.
import WidgetCard from "../../../component/instance/widget";
import StatementSqlStatisticsTable from "../../../component/instance/table/StatementSqlStatisticsTable";
import EventsApi from "../../../api/instance/EventsApi";
import { CHART_DATA_TYPE } from "../../../component/Constants";
import ConditionalRender from "../../../helpers/ConditionalRender";
import MoreStatisticsPicker from "../../../component/instance/MoreStatisticsPicker";
import { useSqlStatisticsSelection } from "../../../hooks/useSqlStatisticsSelection";

export default function SqlStatistics(props: {
    instance: InstanceTarget,
    filterParameters: string,
    statementId: string,
    batchStatementId?: string,
    statementType?: StatementTypeInfo,
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void,
}) {
    const { instance, statementId } = props;
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh
    const { eventTypes } = useEventTypes();
    const [loading, setLoading] = useState<boolean>(true);
    const pathname = window.location.pathname

    // Parse URL parameters for baseline metrics
    const urlParams = new URLSearchParams(window.location.search);
    const avgDurationParam = parseFloat(urlParams.get('avgduration') || '0');
    const baselineAvgDurationParam = parseFloat(urlParams.get('baselineavgduration') || '0');
    const baselineStdDevDurationParam = parseFloat(urlParams.get('baselinestddevduration') || '0');

    const [statementCombinedChanges, setStatementCombinedChanges] = useState<CombinedChanges[]>([]);
    // const [statementChartPlotBands, setStatementChartPlotBands] = useState<ChartPlotBand[]>([]);
    const [statementChartPlotLines, setStatementChartPlotLines] = useState<ChartPlotLine[]>([]);
    const statementType = props.statementType || getStatementTypeFromURL()

    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);
    const [avgDurationSeries, setAvgDurationSeries] = useState<number[][]>([]);
    const [durationRange1Series, setDurationRange1Series] = useState<number[][]>([]);
    const [durationRange2Series, setDurationRange2Series] = useState<number[][]>([]);
    const [durationRange3Series, setDurationRange3Series] = useState<number[][]>([]);

    const {
        supportedStatistics,
        setSupportedStatistics,
        statisticsToShow,
        updateSqlExtendedOptionsList,
        checkUncheckAll
    } = useSqlStatisticsSelection(instance.id, 'statement');

    const [executions, setExecutions] = useState<number>(0);
    const [previousExecutions, setPreviousExecutions] = useState<number>(0);

    const [totalInstanceTime, setTotalInstanceTime] = useState<number>(0);
    const [totalPreviousInstanceTime, setTotalPreviousInstanceTime] = useState<number>(0);

    const timeSeriesPeak = useMemo(() => {
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const executionPeak = useMemo(() => {
        return Helper.getPeakValue(executionsSeries.map(i => i[1]))
    }, [executionsSeries])

    const getExecutions = async (instanceId: number) => {
        const executionsResult = await getStatementExecutions(instanceId, instance.type, statementId, timeRangeContext, props.filterParameters, statementType.isGroup, props.batchStatementId)
        setExecutions(executionsResult.data)
        setPreviousExecutions(executionsResult.previousData)
    }

    const getTotalTime = async (instanceId: number) => {
        const executionsTimeResult = await getInstanceTotalDuration(instanceId, timeRangeContext, instance.type, statementId, statementType.isGroup, props.batchStatementId)
        setTotalInstanceTime(executionsTimeResult.totalInstanceTime)
        setTotalPreviousInstanceTime(executionsTimeResult.totalPreviousInstanceTime)
    }


    const getChanges = async (instanceId: number) => {

        let allEvents: any[] = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instanceId),
            combinedChanges: CombinedChanges[] = [],
            chartPlotLines: ChartPlotLine[] = [];

        for (let index = 0; index < allEvents.length; index++) {
            const currentItem = allEvents[index];
            combinedChanges.push({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            });
        }

        // Set the sort order for the combined changes and events.
        combinedChanges.sort(function compare(a, b) {
            var aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
            var bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
            return bb - aa;
        });

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval)
        chartPlotLines = Helper.getChartPlotLines(buildRedirectQuery(timeRangeContext.timeRange), combinedChanges, chartCategories, interval, false, statementId, eventTypes);
        // const chartPlotBands = Helper.getChartPlotbands(combinedChanges, chartCategories);

        setStatementCombinedChanges(combinedChanges)
        // setStatementChartPlotBands(chartPlotBands)
        setStatementChartPlotLines(chartPlotLines)
    }

    useEffect(() => {
        let isMounted = true;

        async function load() {
            try {
                await getExecutions(instance.id)
                await getTotalTime(instance.id)
                await getChanges(instance.id)
                setLoading(true);
                // const sqlHashString = getAPIString(statementId, props.batchStatementId)
                const sqlHashString = statementType.isGroup ? 'grouphash' : 'sqlhash'
                const results: any[][] = await fetchResults(
                    [
                        SqlApi.fetchStatisticTime(buildApiQuery(timeRangeContext.timeRange), instance.id, instance.type, `&${sqlHashString}=${props.batchStatementId || statementId}`)
                    ]);
                let executions: number[][] = []
                let duration: number[][] = []
                let avgDuration: number[][] = []
                let durationRange1: number[][] = []
                let durationRange2: number[][] = []
                let durationRange3: number[][] = []

                if (active && results[0].length) {
                    const supportedStatisticsList: any[][] = await fetchResults(
                        [
                            SqlApi.fetchStatisticName(props.instance.type, '&collect=true')
                        ]);

                    const filteredSupportedStatistics = supportedStatisticsList[0].filter(key => key !== 'duration' && key !== 'executions');
                    const stats: any[] = []

                    filteredSupportedStatistics.forEach(stat => {
                        stats.push({
                            name: stat,
                            data: results[0].map(item => [getConvertedTimeToUTC(item), item[stat]])
                        });
                    })

                    results[0].forEach(item => {
                        executions.push([getConvertedTimeToUTC(item), item.executions])
                        duration.push([getConvertedTimeToUTC(item), item.duration])


                        // Process avgduration and stddevduration for range charts
                        const timestamp = getConvertedTimeToUTC(item);
                        const avg = item.avgduration || 0;
                        const stddev = item.stddevduration || 0;


                        // Calculate range bounds for 1σ, 2σ, and 3σ, ensuring lower bounds don't go below 0
                        avgDuration.push([timestamp, avg]);
                        durationRange1.push([timestamp, Math.max(0, avg - stddev), avg + stddev]);
                        durationRange2.push([timestamp, Math.max(0, avg - 2 * stddev), avg + 2 * stddev]);
                        durationRange3.push([timestamp, Math.max(0, avg - 3 * stddev), avg + 3 * stddev]);
                    })
                    setSupportedStatistics(stats)
                    setExecutionsSeries(executions)
                    setTimeSeries(duration)
                    setAvgDurationSeries(avgDuration)
                    setDurationRange1Series(durationRange1)
                    setDurationRange2Series(durationRange2)
                    setDurationRange3Series(durationRange3)
                }
                setLoading(false);
            } catch (x: any) {
                console.log(x.message);
                setLoading(false);
            }

            return true;
        }

        let active: boolean = true;
        if (instance && isMounted) {
            setLoading(true);

            const getDataPromise = load();

            getDataPromise.then(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }

        return () => {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, instance.id, instance.type, pathname]);

    const timeChartSeries = useMemo<EChartTimeSeries[]>(() => [
        { name: 'Duration', chartType: 'bar', color: '#69d1bf', data: timeSeries as EChartPoint[] },
        { name: 'Executions', chartType: 'line', color: '#ff853d', data: executionsSeries as EChartPoint[], yAxisIndex: 1 },
    ], [timeSeries, executionsSeries]);

    const timeChart = (
        <TimeSeriesChart series={timeChartSeries} tooltipType={CHART_DATA_TYPE.TIME}
                         plotLines={statementChartPlotLines} legend={true}/>
    );

    const avgDurationPeak = useMemo(() => {
        // Get peak from the 3σ range (which will be the highest)
        const peakFromRange = durationRange3Series.length > 0
            ? Math.max(...durationRange3Series.map(i => i[2]))
            : 0;
        return peakFromRange;
    }, [durationRange3Series]);

    const bandTooltip = useCallback((params: any[]) => {
        let data = '';

        const avgPoint = params.find((point: any) => point.seriesName === 'Average Duration');
        if (avgPoint) {
            data += tooltipRow(avgPoint.color, avgPoint.seriesName, Helper.getTimeInEnglish(avgPoint.value[1]));
        }

        ['Average +1σ', 'Average +2σ', 'Average +3σ'].forEach(rangeName => {
            const point = params.find((entry: any) => entry.seriesName === rangeName);
            if (point && point.data?.high !== undefined) {
                data += tooltipRow(point.color, rangeName, Helper.getTimeInEnglish(point.data.high));
            }
        });

        return wrapTooltip(params?.[0]?.axisValue, data);
    }, []);

    const avgDurationSeriesList = useMemo<EChartTimeSeries[]>(() => [
        { name: 'Average Duration', chartType: 'line', color: '#69d1bf', data: avgDurationSeries as EChartPoint[] },
        { name: 'Average +1σ', chartType: 'band', color: '#c8e6c9', fillOpacity: 0.5, data: durationRange1Series as EChartBandPoint[] },
        { name: 'Average +2σ', chartType: 'band', color: '#ffd699', fillOpacity: 0.4, data: durationRange2Series as EChartBandPoint[] },
        { name: 'Average +3σ', chartType: 'band', color: '#ffcccc', fillOpacity: 0.3, data: durationRange3Series as EChartBandPoint[] },
    ], [avgDurationSeries, durationRange1Series, durationRange2Series, durationRange3Series]);

    const avgDurationChart = (
        <TimeSeriesChart series={avgDurationSeriesList} tooltipType={CHART_DATA_TYPE.TIME}
                         plotLines={statementChartPlotLines} legend={true}
                         tooltipFormatter={bandTooltip}/>
    );

    // Check if we have baseline parameters from URL
    const hasBaselineParams = avgDurationParam > 0 || baselineAvgDurationParam > 0;

    const avgDurationFlatLine = useMemo(() => {
        if (avgDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [item[0], avgDurationParam]);
        }
        return [];
    }, [avgDurationSeries, avgDurationParam]);

    const baselineAvgDurationFlatLine = useMemo(() => {
        if (baselineAvgDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [item[0], baselineAvgDurationParam]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam]);

    // Create flat shaded areas for baseline standard deviations (ABOVE baseline)
    const baselineFlatRange1Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    const baselineFlatRange2Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + 2 * baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    const baselineFlatRange3Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + 3 * baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    // Calculate peak for baseline comparison chart
    // Baseline Comparison Chart
    const baselineComparisonChart = useMemo(() => {
        if (!hasBaselineParams || avgDurationSeries.length === 0) {
            return null;
        }

        const series: EChartTimeSeries[] = [
            {
                chartType: 'line',
                color: '#69d1bf',
                data: avgDurationSeries as EChartPoint[],
                name: 'Average Duration',
            },
        ];

        if (baselineAvgDurationFlatLine.length > 0) {
            series.push({
                chartType: 'line',
                color: '#ff9800',
                data: baselineAvgDurationFlatLine as EChartPoint[],
                name: 'Baseline',
            });
        }

        if (baselineFlatRange1Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#c8e6c9',
                data: baselineFlatRange1Series as EChartBandPoint[],
                name: 'Baseline +1σ',
                fillOpacity: 0.5,
            });
        }

        if (baselineFlatRange2Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#ffd699',
                data: baselineFlatRange2Series as EChartBandPoint[],
                name: 'Baseline +2σ',
                fillOpacity: 0.4,
            });
        }

        if (baselineFlatRange3Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#ffcccc',
                data: baselineFlatRange3Series as EChartBandPoint[],
                name: 'Baseline +3σ',
                fillOpacity: 0.3,
            });
        }

        if (avgDurationFlatLine.length > 0) {
            series.push({
                chartType: 'line',
                color: '#9c27b0',
                data: avgDurationFlatLine as EChartPoint[],
                name: 'Average Duration (whole period)',
            });
        }

        return <TimeSeriesChart series={series} tooltipType={CHART_DATA_TYPE.TIME}
                                plotLines={statementChartPlotLines} legend={true}
                                tooltipFormatter={bandTooltip}/>;

    }, [hasBaselineParams, avgDurationSeries, avgDurationFlatLine, baselineAvgDurationFlatLine, baselineFlatRange1Series, baselineFlatRange2Series, baselineFlatRange3Series, statementChartPlotLines, bandTooltip]);

    const getChart = ((statisticData: { name: string, data: [] }) => (
        <TimeSeriesChart key={statisticData.name}
                         series={[{ name: statisticData.name, chartType: 'bar', data: statisticData.data as EChartPoint[] }]}
                         tooltipType={CHART_DATA_TYPE.TIME} plotLines={statementChartPlotLines}/>
    ));

    const changes = statementCombinedChanges.filter(item => item.change);
    const events = statementCombinedChanges.filter(item => item.custom);
    const alerts = statementCombinedChanges.filter(item => item.alert);

    return (<>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
            <WidgetCard
                name="Total Time"
                type='time'
                iconClassName="time"
                icon="fa-stopwatch"
                value={totalInstanceTime}
                previous={totalPreviousInstanceTime}
                cardTooltipText="The total time spent"
            />

            <WidgetCard
                name="Executions"
                iconClassName="executions"
                icon="fa-chart-line"
                value={executions}
                previous={previousExecutions}
                cardTooltipText="The total number of executions"
            />

            <WidgetCard
                name="Average Time"
                iconClassName="averagetime"
                icon="fa-clock"
                value={executions > 0 ? totalInstanceTime / executions : 0}
                previous={previousExecutions > 0 ? totalPreviousInstanceTime / previousExecutions : undefined}
                cardTooltipText="The average time per execution"
            />

            <WidgetCard
                name="Events"
                events={{
                    alerts: alerts.length,
                    changes: changes.length,
                    custom: events.length
                }}
            />
        </div>

        <div className="row row-cols-1">
            <div id="statements" className="tab-pane fade show active" role="tabpanel"
                aria-labelledby="statements-tab">

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                        Duration (ms) and Executions
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                            data-bs-target={"#collapse-duration"} aria-expanded="false"
                            aria-controls={"collapse-duration"} />
                        <MoreStatisticsPicker
                            supportedStatistics={supportedStatistics}
                            statisticsToShow={statisticsToShow}
                            onToggle={updateSqlExtendedOptionsList}
                            onCheckUncheckAll={checkUncheckAll}
                        />
                    </div>

                    <div id={"collapse-duration"} className="card-body collapse show">

                        <p className="chart-peak">
                            <React.Fragment>Peak
                                Time: {Helper.getTimeInEnglish(timeSeriesPeak)}</React.Fragment>,&nbsp;
                            <React.Fragment>Peak
                                Executions: {executionPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</React.Fragment>
                        </p>
                        <br />

                        <ConditionalRender if={loading && !isAutorefresh}>
                            <div className="w-100 text-center time-chart-center text-muted mt-3">
                                <div className="loader spinner chartSpinner"></div>
                                <p>Loading data...</p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={isAutorefresh || !loading}>
                            {timeChart}
                        </ConditionalRender>

                    </div>
                </div>

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                        Average Duration with Standard Deviation
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                            data-bs-target={"#collapse-avg-duration"} aria-expanded="false"
                            aria-controls={"collapse-avg-duration"} />
                    </div>

                    <div id={"collapse-avg-duration"} className="card-body collapse show">
                        <ConditionalRender if={loading && !isAutorefresh}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={isAutorefresh || !loading}>
                            {avgDurationSeries.length > 0 && (
                                <>
                                    <p className="chart-peak">
                                        <React.Fragment>Peak Average Duration: {Helper.getTimeInEnglish(avgDurationPeak)}</React.Fragment>
                                    </p>
                                    <br />
                                    {avgDurationChart}
                                </>
                            )}
                            {avgDurationSeries.length === 0 && !loading && (
                                <p className="text-muted">No average duration data available</p>
                            )}
                        </ConditionalRender>
                    </div>
                </div>

                {hasBaselineParams && (
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            Baseline Comparison
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target={"#collapse-baseline-comparison"} aria-expanded="false"
                                aria-controls={"collapse-baseline-comparison"} />
                        </div>

                        <div id={"collapse-baseline-comparison"} className="card-body collapse show">
                            <ConditionalRender if={loading && !isAutorefresh}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={isAutorefresh || !loading}>
                                {avgDurationSeries.length > 0 && (
                                    <>
                                        <p className="chart-peak">
                                            {avgDurationParam > 0 && (
                                                <React.Fragment>Avg Duration (whole period): {Helper.getTimeInEnglish(avgDurationParam)}</React.Fragment>
                                            )}
                                            {avgDurationParam > 0 && baselineAvgDurationParam > 0 && ', '}
                                            {baselineAvgDurationParam > 0 && (
                                                <React.Fragment>Baseline: {Helper.getTimeInEnglish(baselineAvgDurationParam)}</React.Fragment>
                                            )}
                                            {baselineStdDevDurationParam > 0 && (
                                                <React.Fragment>, Baseline Std Dev: {Helper.getTimeInEnglish(baselineStdDevDurationParam)}</React.Fragment>
                                            )}
                                        </p>
                                        <br />
                                        {baselineComparisonChart}
                                    </>
                                )}
                                {avgDurationSeries.length === 0 && !loading && (
                                    <p className="text-muted">No duration data available</p>
                                )}
                            </ConditionalRender>
                        </div>
                    </div>
                )}

                {statisticsToShow.map(stat =>
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            {stat.name.replaceAll('_', ' ')}
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target={`#${stat.name}`} aria-expanded="false"
                                aria-controls={stat.name} />
                        </div>
                        <div id={stat.name} className="card-body collapse show mt-5">
                            {getChart(stat)}
                        </div>
                    </div>)}

                <StatementSqlStatisticsTable instance={props.instance} statementId={statementId} batchStatementId={props.batchStatementId} filterParameters={props.filterParameters} statementType={statementType} onStatementClick={props.onStatementClick}/>
            </div>
        </div>
    </>
    )
}
